import React from "react";
import { ModalForm, useModalDispatch } from "collections/Modal";
import {
  Field,
  FieldProps,
  Form,
  shortCodeString,
  TextArea
} from "collections/Form";
import { InferType, object } from "yup";
import { useTranslation } from "utilities/i18n";
import { useReleaseInstanceMutation } from "generated/graphql";
import { DISMISS, useNotification } from "collections/Notifications";
import { InstanceModel } from "utilities/licenseServerUtils";
import { CancelButton } from "elements/CancelButton/CancelButton";
import { DetailsLoader, SubmitButton } from "elements";
import { PartialExcept } from "utilities/types";

const ProcessAltLicenseAwkSchema = object().shape({
  requestValue: shortCodeString()
    .trim()
    .label("ENCODED_RELEASE_CODE")
    .required()
});

type ProcessLicenseAwkFormProps = InferType<typeof ProcessAltLicenseAwkSchema>;

export const ReleaseLicenseModal: React.FC<{
  refresh: () => void;
  instances: Array<InstanceModel>;
}> = ({ instances, refresh }) => {
  const { t } = useTranslation();
  const [, releaseInstance] = useReleaseInstanceMutation();
  const closeModal = useModalDispatch();
  const [addNotification] = useNotification();

  const first: PartialExcept<InstanceModel, "recordId"> =
    instances?.length > 0
      ? instances[0]
      : {
          recordId: ""
        };

  const initialValues: ProcessLicenseAwkFormProps = {
    requestValue: ""
  };

  const onSubmit = async (values: ProcessLicenseAwkFormProps) => {
    const licenseResponse = await releaseInstance({
      recordId: first.recordId,
      requestValue: values.requestValue
    });

    if (licenseResponse.data?.releaseInstance) {
      closeModal();
      refresh();

      addNotification({
        type: "success",
        dismiss: DISMISS.DEFAULT,
        message: t("SUCCESSFULLY_RELEASED_LICENSE"),
        persistThroughNav: true
      });
    } else if (licenseResponse.error) {
      addNotification({
        type: "error",
        dismiss: DISMISS.DEFAULT,
        message: t("RELEASE_LICENSE_ERROR", {
          message: licenseResponse.error.message
        })
      });
    }
  };

  return (
    <ModalForm
      title={t("RELEASE_LICENSE")}
      initialValues={initialValues}
      onSubmit={onSubmit}
      name={"ReleaseLicenseModal"}
      validationSchema={ProcessAltLicenseAwkSchema}
    >
      {({ isSubmitting }) => (
        <Form>
          <DetailsLoader loading={isSubmitting} title={t("RELEASE_LICENSE")}>
            <div className="form-content">
              <label htmlFor="editor" className="sr-only-ignore">
                {t("ENCODED_RELEASE_CODE")}
              </label>
              <TextArea
                id="editor"
                name="requestValue"
                errorAbove={true}
                className="details"
              />
            </div>
          </DetailsLoader>
          <div className="btn-container">
            <CancelButton onClick={closeModal} />
            <Field name="submit">
              {({ form: { dirty, isSubmitting, isValid } }: FieldProps) => (
                <SubmitButton
                  isSubmitting={isSubmitting}
                  testId="save"
                  disabled={!isValid || !dirty}
                  labelKey="RELEASE_LICENSE"
                  submittingLabelKey="RELEASING_LICENSE"
                />
              )}
            </Field>
          </div>
        </Form>
      )}
    </ModalForm>
  );
};
